// Fahrtenzahl eines Fahrers: abgeschlossene Fahrten insgesamt.
//
// Fahrer-Dokumente (users) tragen kein Feld `total_rides` – die Zahl muss aus
// der rides-Sammlung ermittelt werden. Statt die Dokumente zu laden (teuer und
// bisher auf 100 gedeckelt, also falsch fuer erfahrene Fahrer) wird die
// Aggregations-Abfrage getCountFromServer genutzt: ein Read pro 1000 gezaehlter
// Fahrten, ohne Obergrenze.
//
// Gezaehlt werden nur abgeschlossene Fahrten ('completed' und die archivierte
// Variante 'archived') – Stornos sind keine gefahrenen Fahrten. Die Abfrage
// (driverId ==, status in) wird vom bestehenden Index (driverId, status)
// abgedeckt, es ist kein neuer Firestore-Index noetig.

import { collection, getCountFromServer, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';

export const COMPLETED_RIDE_STATUSES = ['completed', 'archived'];

// Zaehlung ist teuer genug, um sie nicht bei jedem Snapshot-Update zu
// wiederholen – dieselbe Cache-Dauer wie im drivers-Slice.
const CACHE_DURATION = 5 * 60 * 1000;
const MAX_PARALLEL_COUNTS = 10;

const countCache = new Map();

export const countCompletedRidesForDriver = async (driverId) => {
  const snapshot = await getCountFromServer(query(
    collection(db, 'rides'),
    where('driverId', '==', driverId),
    where('status', 'in', COMPLETED_RIDE_STATUSES)
  ));
  return snapshot.data().count;
};

const countWithCache = async (driverId, forceRefresh) => {
  const cached = countCache.get(driverId);
  if (!forceRefresh && cached && Date.now() - cached.fetchedAt < CACHE_DURATION) {
    return cached.count;
  }
  try {
    const count = await countCompletedRidesForDriver(driverId);
    countCache.set(driverId, { count, fetchedAt: Date.now() });
    return count;
  } catch (error) {
    console.warn(`Fehler beim Zaehlen der Fahrten fuer Fahrer ${driverId}:`, error);
    // Alten Wert behalten, damit die Liste bei einem Fehlschlag nicht leer wird.
    return cached ? cached.count : null;
  }
};

// Liefert { driverId: count } fuer alle uebergebenen Fahrer. Fahrer, deren
// Zaehlung fehlschlaegt, fehlen im Ergebnis (Liste zeigt dann "–").
export const fetchCompletedRideCounts = async (driverIds, { forceRefresh = false } = {}) => {
  const counts = {};
  const ids = [...new Set(driverIds.filter(Boolean))];

  for (let i = 0; i < ids.length; i += MAX_PARALLEL_COUNTS) {
    const chunk = ids.slice(i, i + MAX_PARALLEL_COUNTS);
    const results = await Promise.all(chunk.map((id) => countWithCache(id, forceRefresh)));
    chunk.forEach((id, index) => {
      if (results[index] != null) counts[id] = results[index];
    });
  }

  return counts;
};

export const clearDriverRideCountCache = () => countCache.clear();
